import React from "react";
import { Text, Pressable, StyleSheet, ActivityIndicator } from "react-native";
import { APP_TOKENS as tokens } from "../../../styles/tokens";

interface PrimaryButtonProps {
  text: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  roundBorderRadius?: boolean;
  width?: number;
  icon?: React.ReactNode;
  style?: any;
  accessibilityLabel?: string;
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  text,
  onPress,
  disabled = false,
  loading = false,
  fullWidth = false,
  roundBorderRadius = false,
  width = -1,
  icon = null,
  style = null,
  accessibilityLabel,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const isDisabled = disabled || loading;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || text || "Button"}
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      style={[
        styles.button,
        isFocused && styles.focused,
        isDisabled && styles.disabledButton,
        fullWidth && { width: "100%" },
        roundBorderRadius && { borderRadius: tokens.radius.round },
        width !== -1 && { width: width },
        { height: text ? 46 : 36 },
        {
          paddingVertical: text ? tokens.spacing.md : tokens.spacing.sm,
          paddingHorizontal: text ? tokens.spacing.xxl : tokens.spacing.sm,
        },
        text === "" && styles.iconOnly,
        style,
      ]}
      onPress={onPress}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={isDisabled}
    >
      {loading ? (
        <ActivityIndicator size="small" color={tokens.color.inverse} />
      ) : (
        <>
          {icon ? icon : null}
          {text && <Text style={styles.text}>{text}</Text>}
        </>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: tokens.color.accent,
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.xxl,
    borderRadius: tokens.radius.button,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.spacing.md,
  },
  text: {
    fontFamily: tokens.font.medium,
    textAlign: "center",
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    letterSpacing: 0.032,
    fontStyle: "normal",
    color: tokens.color.inverse,
    userSelect: "none",
  },
  focused: {
    outlineColor: tokens.color.accentDark,
    outlineOffset: tokens.spacing.xs,
    outlineStyle: "solid",
    outlineWidth: tokens.borderWidth.strong,
  } as any,
  disabledButton: {
    backgroundColor: tokens.color.borderStrong,
    opacity: tokens.opacity.disabled,
  },
  iconOnly: {
    width: tokens.spacing.huge + tokens.spacing.xs,
    minWidth: tokens.spacing.huge + tokens.spacing.xs,
    minHeight: tokens.spacing.huge + tokens.spacing.xs,
    height: tokens.spacing.huge + tokens.spacing.xs,
  },
  disabledText: {
    color: tokens.color.textMuted,
  },
});

export default PrimaryButton;
